import * as THREE from 'three/webgpu';
import {Observable, Subject, Subscription} from 'rxjs';
import {SketchDefinitionChangeHub} from '../../feature/rebuild/SketchDefinitionChangeHub';
import {
    clearSketchContourDisplays,
    createSketchCloneContext,
    SketchGroup,
    type SketchGroupEntityRemovedEvent,
} from '../../mesh/sketch';
import type {ReferencePlane} from '../../reference/ReferencePlane';
import type {Viewer} from '../Viewer';
import {EntityInteractionEventType} from '../../manager/EntitySelectionManager';
import {SketchScaleController} from './sketch-scale/SketchScaleController';
import {
    isAbstractSketchEdgeObject,
    isSketchCollectionObject,
    isSketchPointObject,
    isSketchSlotObject,
} from '../../mesh/guards';
//------ 草图离线包定义持久化 开始------
import {SketchDimensionRepository} from '../../sketch/dimension/repository/SketchDimensionRepository';
import {SketchDimensionSketchDuplicationService} from '../../sketch/dimension/application/SketchDimensionSketchDuplicationService';
import {SketchDimensionRelationViewSynchronizer} from '../../sketch/dimension/relation/SketchDimensionRelationViewSynchronizer';
import {SKETCH_DIMENSION_SNAPSHOT_SCHEMA_VERSION_V1} from '../../sketch/dimension/SketchDimensionTypes';
import type {SketchGroupDuplicationResult} from '../../../types/sketch/dimension/SketchGroupDuplicationTypes';
import {SketchGroupPersistenceSerializer} from '../../sketch/persistence/SketchGroupPersistenceSerializer';
import {SketchGroupPersistenceDeserializer} from '../../sketch/persistence/SketchGroupPersistenceDeserializer';
import {SketchMirrorAxisReferenceResolver} from '../../sketch/entities-transform/SketchMirrorAxisReferenceResolver';
import type {
    PuppySketchExtension,
    PuppySketchExtensionEntry,
} from '../../export/puppy/puppySketchExtension';
//------ 草图离线包定义持久化 结束------

/**
 * 草图组创建选项。
 */
export interface SketchGroupCreateOptions {
    /** 关联的基准面实例。 */
    referencePlane: ReferencePlane;
}
export enum SketchDisplayState {
    /** 编辑态：当前正在编辑的草图。 */
    Edit = 'edit',
    /** 展示态：草图保留可见，但不处于编辑流程中。 */
    Shown = 'shown',
    /** 隐藏态：草图默认不参与可见展示。 */
    Hidden = 'hidden',
}

/**
 * 草图组生命周期事件类型。
 */
export enum SketchGroupLifecycleEventType {
    /** 草图组已创建并进入编辑态。 */
    Created = 'created',
    /** 草图组已结束编辑并保留在场景中。 */
    Ended = 'ended',
    /** 草图组已从场景中移除。 */
    Removed = 'removed',
    /** 草图组显示态已变化。 */
    DisplayStateChanged = 'display-state-changed',
    /** 草图转换实体引用的派生诊断状态已变化。 */
    ReferenceDiagnosticChanged = 'reference-diagnostic-changed',
}

/**
 * 草图组生命周期事件。
 */
export interface SketchGroupLifecycleEvent {
    /** 事件类型。 */
    type: SketchGroupLifecycleEventType;
    /** 草图组 ID。 */
    sketchId: string;
    /** 跨运行时稳定的草图持久 ID。 */
    persistentSketchId: string;
    /** 草图组名称。 */
    sketchName: string;
    /** 当前显示态。 */
    displayState: SketchDisplayState | null;
    /** 当前活跃草图组 ID。 */
    activeSketchId: string | null;
}

/**
 * 草图组结束结果。
 */
export interface SketchGroupEndResult {
    /** 已结束的草图组 ID。 */
    endedGroupId: string | null;
    /** 已结束的草图组实例。 */
    endedGroup: SketchGroup | null;
    /** 是否已从场景中移除。 */
    removed: boolean;
    /** 是否存在待提交的定义变更。 */
    shouldCommitDefinitionChanges: boolean;
    /** 结束后的显示态。 */
    displayState: SketchDisplayState | null;
    /** 显示态是否在结束过程中发生变化。 */
    displayStateChanged: boolean;
}

/**
 * 判断草图组是否已经承载真实草图实体。
 *
 * 设计说明：
 * 1. 当前分支中的草图图元都已经被 `SketchGroup` 统一追踪；
 * 2. 会话收口时只需要判断是否存在被管理的草图对象，而不必再次遍历整棵对象树；
 * 3. 这样可以把“空草图是否应丢弃”的规则集中在会话管理层。
 *
 * @param group 草图组。
 * @returns 是否存在真实草图实体。
 */
function hasManagedSketchEntities(group: SketchGroup): boolean {
    return group.getSketchObjects().size > 0;
}
/**
 * 草图组管理器。
 *
 * 职责：
 * 1. 管理草图组（SketchGroup）的创建和销毁；
 * 2. 维护当前活跃草图组和所有草图的显示状态；
 * 3. 提供草图实体添加的统一入口。
 */
export class SketchGroupManager {
    /** Viewer 实例。 */
    private readonly _viewer: Viewer;
    /** 草图定义变更中心。 */
    public readonly definitionChangeHub: SketchDefinitionChangeHub;
    /** 草图缩放控制器。 */
    public readonly sketchScaleController: SketchScaleController;
    /** 当前正在编辑的草图组。 */
    private _activeSketchGroup: SketchGroup | null = null;
    /**
     * 当前活动草图是否由本次会话新建。
     *
     * `discardEmpty` 只用于取消尚未形成内容的新草图；
     * 已存在草图即使被用户删空，也必须保留其身份和下游 Feature 引用关系。
     */
    private activeSketchGroupCreatedInCurrentSession = false;
    //------ 草图组索引能力 开始------
    /** 已创建草图组映射表。 */
    private readonly _groups: Map<string, SketchGroup> = new Map();
    /**
     * 草图组在设计树中的稳定顺序。
     *
     * 设计说明：
     * 1. Map 的插入顺序会在删除后恢复时漂移，不适合作为 CAD 文档树顺序；
     * 2. Undo/Redo 恢复同一个 sketchId 时必须回到原位置，避免“草图1”索引持续变化；
     * 3. 该数组只保存 ID，不持有额外 Object3D 引用，避免影响 Three.js 生命周期。
     */
    private readonly _groupOrder: string[] = [];

    //------ 草图离线包定义持久化 开始------
    /** 草图持久化序列化器（无状态，可复用）。 */
    private readonly sketchPersistenceSerializer = new SketchGroupPersistenceSerializer();
    /** 草图持久化反序列化器；通过 Viewer resolver 恢复外部镜像轴。 */
    private readonly sketchPersistenceDeserializer: SketchGroupPersistenceDeserializer;
    //------ 草图离线包定义持久化 结束------
    //------ 草图智能尺寸领域仓库 开始------
    /**
     * 草图尺寸仓库（纯领域主状态）。
     *
     * 设计说明：
     * 1. 尺寸聚合的唯一事实来源，随 SketchGroupManager 生命周期存在；
     * 2. 运行时草图组销毁（Undo/Redo 克隆恢复）不联动清空尺寸——尺寸主状态
     *    由尺寸历史 adapter 负责恢复；真实“删除草图”命令的尺寸联动删除
     *    由后续命令层（阶段 4+）走应用服务提交同一笔事务。
     */
    public readonly dimensionRepository = new SketchDimensionRepository();
    /** 整草图智能尺寸复制服务。 */
    private readonly sketchDimensionDuplicationService = new SketchDimensionSketchDuplicationService();
    /** 智能尺寸运行时关系视图同步器。 */
    private readonly sketchDimensionRelationSynchronizer = new SketchDimensionRelationViewSynchronizer();
    //------ 草图智能尺寸领域仓库 结束------
    /**
     * 草图组在设计树中的创建时间戳（毫秒）。
     *
     * 设计说明：
     * 1. 与 `_groupOrder` 解耦，专门服务设计树与特征/基准面的时间线混排；
     * 2. 创建后不可变，Undo/Redo 恢复时必须写回历史快照中的原始值；
     * 3. 只保存 ID 到时间戳映射，不持有 Object3D 引用。
     */
    private readonly _groupCreatedAt = new Map<string, number>();
    /**
     * 下一个真实草图默认名称序号。
     *
     * 设计说明：
     * 1. 该序号只服务设计树中真实创建的草图；
     * 2. 历史快照、运行时恢复和临时克隆不能推进该序号；
     * 3. 恢复旧草图时会从既有名称同步上界，避免后续新草图撞名。
     */
    private _nextSketchGroupNameIndex = 1;
    //------ 草图组索引能力 结束------

    /** 所有草图组的显示状态映射（key: group.uuid）。 */
    private readonly _displayStates: Map<string, SketchDisplayState> = new Map();
    /** 草图组生命周期事件流。 */
    private readonly _lifecycleSubject: Subject<SketchGroupLifecycleEvent> = new Subject<SketchGroupLifecycleEvent>();
    /** 草图实体移除事件流。 */
    private readonly _entityRemovedSubject: Subject<SketchGroupEntityRemovedEvent> = new Subject<SketchGroupEntityRemovedEvent>();
    /** 草图实体移除监听器映射。 */
    private readonly _entityRemovedListenerBySketchId: Map<string, (event: unknown) => void> = new Map();
    /** 内部订阅列表。 */
    private readonly _subscriptions: Subscription[] = [];

    /**
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this._viewer = viewer;
        this.sketchPersistenceDeserializer = new SketchGroupPersistenceDeserializer({
            mirrorAxisReferenceResolver: new SketchMirrorAxisReferenceResolver(viewer),
        });
        this.definitionChangeHub = new SketchDefinitionChangeHub();
        this.sketchScaleController = new SketchScaleController(viewer);
        this.sketchScaleController.start();
        this.bindSelectionEvents();
    }

    /**
     * 获取当前活跃的草图组。
     */
    public get activeSketchGroup(): SketchGroup | null {
        return this._activeSketchGroup;
    }

    /**
     * 获取显示状态映射表（只读访问）。
     */
    public get displayStates(): ReadonlyMap<string, SketchDisplayState> {
        return this._displayStates;
    }

    /**
     * 判断全部已管理草图是否存在非几何约束引用目标。
     *
     * @returns 至少存在一个外部镜像轴 provider 时返回 true。
     */
    public hasSketchReferenceTargets(): boolean {
        return this.getAllGroups().some((group) => group.getSketchReferenceTargets().size > 0);
    }

    /**
     * 监听草图组生命周期事件。
     *
     * @returns 生命周期事件流。
     */
    public observeLifecycle(): Observable<SketchGroupLifecycleEvent> {
        return this._lifecycleSubject.asObservable();
    }

    /**
     * 通知宿主指定草图的转换实体引用诊断已经变化。
     *
     * dangling 状态属于引用仓库主事实的派生投影，不改变草图定义，也不进入 Undo/Redo；
     * 这里复用草图组事件流，只负责让设计树等宿主重新读取权威摘要。
     *
     * @param sketchIds 草图运行时 ID 或持久 ID。
     */
    public notifyReferenceDiagnosticChanged(sketchIds: readonly string[]): void {
        const publishedGroupIds = new Set<string>();
        sketchIds.forEach((sketchId) => {
            const group = this.getGroupById(sketchId);
            if (!group || publishedGroupIds.has(group.uuid)) {
                return;
            }
            publishedGroupIds.add(group.uuid);
            this.publishLifecycleEvent(
                group,
                SketchGroupLifecycleEventType.ReferenceDiagnosticChanged,
            );
        });
    }

    /**
     * 监听任意草图组中的实体移除事件。
     *
     * 设计说明：
     * 1. 删除行为可能来自键盘、修剪、属性面板等不同入口；
     * 2. 管理器作为所有草图组的索引拥有者，负责把各组事件汇聚成单一事件流；
     * 3. 引用系统订阅该流即可完成删除级联，不需要耦合具体 UI 命令。
     *
     * @returns 草图实体移除事件流。
     */
    public observeEntityRemoved(): Observable<SketchGroupEntityRemovedEvent> {
        return this._entityRemovedSubject.asObservable();
    }

    //------ 草图组索引能力 开始------
    /**
     * 获取全部草图组。
     * 说明：
     * 1. 返回创建顺序稳定的草图组列表；
     * 2. 直接作为上层特征树的数据源；
     * 3. 不再要求调用方自行遍历场景检索草图组。
     * @returns 草图组列表。
     */
    public getAllGroups(): SketchGroup[] {
        const orderedGroups = this._groupOrder
            .map((groupId) => this._groups.get(groupId) ?? null)
            .filter((group): group is SketchGroup => Boolean(group));
        const orderedGroupIds = new Set(orderedGroups.map((group) => group.uuid));
        const orphanGroups = Array.from(this._groups.values())
            .filter((group) => !orderedGroupIds.has(group.uuid));

        return [...orderedGroups, ...orphanGroups];
    }

    /**
     * 把任意来源的草图组 ID 归一为运行时 uuid。
     *
     * 设计说明：
     * 1. 管理器内部的顺序表、创建时间表与显示态表都以运行时 uuid 为键，
     *    而特征绑定、离线包载荷传入的是草图持久 ID；
     * 2. 若不在门面入口统一归一，持久 ID 会写出永远不被消费的幽灵表项，表现为“设置了却没生效”；
     * 3. 未登记的 ID 保持原值，兼容“先写显示态、后挂载草图组”的既有时序。
     *
     * @param groupId 草图组 ID，可能是运行时 uuid 或持久 ID。
     * @returns 归一后的运行时 uuid；无法归一时返回原值。
     */
    private resolveRuntimeGroupId(groupId: string): string {
        return this.getGroupById(groupId)?.uuid ?? groupId;
    }

    /**
     * 获取草图组在设计树中的稳定顺序。
     *
     * @param groupId 草图组 ID。
     * @returns 当前设计树顺序；未命中时返回 null。
     */
    public getGroupOrder(groupId: string): number | null {
        const order = this._groupOrder.indexOf(this.resolveRuntimeGroupId(groupId));
        return order >= 0 ? order : null;
    }

    /**
     * 读取草图组在设计树中的创建时间戳。
     *
     * @param groupId 草图组 ID。
     * @returns 创建时间戳（毫秒）；未登记时返回 `undefined`。
     */
    public getGroupCreatedAt(groupId: string): number | undefined {
        return this._groupCreatedAt.get(this.resolveRuntimeGroupId(groupId));
    }

    /**
     * 通过 ID 获取草图组。
     * @param groupId 草图组 ID。
     * @returns 草图组实例，不存在时返回 null。
     */
    public getGroupById(groupId: string): SketchGroup | null {
        const runtimeMatched = this._groups.get(groupId);
        if (runtimeMatched) {
            return runtimeMatched;
        }
        /**
         * 跨会话引用（特征绑定、离线包载荷）按草图持久 ID 定位；运行时 uuid 未命中时按持久 ID 兜底，
         * 保证“唯一业务身份”在所有查询入口都可解析，无需调用方分流。
         */
        let persistentMatched: SketchGroup | null = null;
        this._groups.forEach((group) => {
            if (persistentMatched === null && group.persistentId === groupId) {
                persistentMatched = group;
            }
        });
        return persistentMatched;
    }

    /**
     * 复制指定草图组。
     *
     * 设计说明：
     * 1. 草图复制属于草图组集合管理职责，应由管理器统一维护索引、显示态与生命周期事件；
     * 2. 新副本默认进入展示态，避免继承源草图的隐藏态后导致用户误以为复制失败；
     * 3. 复制后的草图组与源草图完全解耦，只共享参考平面语义，不共享几何对象引用；
     * 4. 智能尺寸由 `duplicateGroupWithDimensions` 统一换新身份并重建关系视图。
     *
     * @param groupId 源草图组 ID。
     * @returns 新草图组；未命中时返回 null。
     */
    public duplicateGroup(groupId: string): SketchGroup | null {
        return this.duplicateGroupWithDimensions(groupId)?.group ?? null;
    }

    /**
     * 复制指定草图组及其全部智能尺寸。
     *
     * 设计说明：
     * 1. 草图对象、尺寸、参数和尺寸链分组都换成新身份，源与副本可以独立编辑；
     * 2. 尺寸关系视图不是关系对象，不随源草图克隆，由新尺寸仓库重新投影；
     * 3. 返回尺寸创建补丁，由宿主层与草图组 create patch 合并为一个模型历史事务；
     * 4. 任一尺寸复制或关系同步失败时，统一回收副本草图和已创建尺寸。
     *
     * @param groupId 源草图组 ID。
     * @returns 草图组与尺寸补丁；未命中源草图时返回 null。
     */
    public duplicateGroupWithDimensions(groupId: string): SketchGroupDuplicationResult | null {
        const sourceGroup = this.getGroupById(groupId);
        if (!sourceGroup) {
            return null;
        }
        /** 尺寸派生视图不在关系容器中，复制上下文只需克隆真实几何关系。 */
        const cloneContext = createSketchCloneContext();
        const duplicatedGroup = sourceGroup.cloneSelf(cloneContext);
        //------ 草图持久身份索引 开始------
        // 设计树复制产出的是新逻辑草图；临时映射只用于本次尺寸引用换绑，不写入任何主状态。
        const persistentIdMap = duplicatedGroup.regeneratePersistentIdentity();
        //------ 草图持久身份索引 结束------
        duplicatedGroup.name = this.allocateSketchGroupName();
        duplicatedGroup.visible = true;
        this._viewer.scene.add(duplicatedGroup);
        this._groups.set(duplicatedGroup.uuid, duplicatedGroup);
        this.registerGroupOrder(duplicatedGroup.uuid);
        this.registerGroupCreatedAt(duplicatedGroup.uuid);
        this._displayStates.set(duplicatedGroup.uuid, SketchDisplayState.Shown);
        this.applyDisplayStateToGroup(duplicatedGroup, SketchDisplayState.Shown);
        this.definitionChangeHub.registerGroup(duplicatedGroup);
        this.bindGroupEntityRemoved(duplicatedGroup);
        this.publishLifecycleEvent(duplicatedGroup, SketchGroupLifecycleEventType.Created);

        try {
            const dimensionResult = this.sketchDimensionDuplicationService.duplicate({
                repository: this.dimensionRepository,
                sourceSketchId: sourceGroup.persistentId,
                targetSketchId: duplicatedGroup.persistentId,
                persistentIdMap,
            });

            // 以目标尺寸仓库为唯一真相重建只读关系视图。
            this.sketchDimensionRelationSynchronizer.synchronize(
                duplicatedGroup.persistentId,
                duplicatedGroup,
                this.dimensionRepository,
            );

            return {
                group: duplicatedGroup,
                ...dimensionResult,
            };
        } catch (error) {
            this.discardDuplicatedGroup(duplicatedGroup.uuid);
            throw error;
        }
    }

    /**
     * 丢弃尚未进入历史栈的草图副本。
     *
     * 该入口只服务复制事务失败回滚：尺寸主状态必须先于场景草图一并清理，
     * 普通设计树删除仍由正式删除命令及其尺寸历史补丁负责。
     *
     * @param groupId 待丢弃副本的运行时或持久 ID。
     * @returns 是否成功移除草图组。
     */
    public discardDuplicatedGroup(groupId: string): boolean {
        const group = this.getGroupById(groupId);

        if (!group) {
            return false;
        }

        this.dimensionRepository.deleteSketch(group.persistentId);
        return this.removeGroup(group.uuid);
    }

    /**
     * 将历史快照中的草图组重新注册回运行时。
     *
     * 设计说明：
     * 1. 设计树删除草图后，undo 需要把同一个 sketchId 的草图组重新挂回 scene；
     * 2. 注册逻辑必须由 SketchGroupManager 统一维护索引、显示态、定义监听和生命周期事件；
     * 3. 调用方传入的草图组必须已经是快照克隆体，管理器只接管它，不再二次克隆。
     *
     * @param group 待恢复的草图组。
     * @param displayState 快照捕获时的显示态。
     * @param options
     * @returns 实际注册的草图组；若同 ID 草图已存在则返回现有实例。
     */
    public restoreGroup(
        group: SketchGroup,
        displayState: SketchDisplayState | null = SketchDisplayState.Shown,
        options: {
            order?: number | null;
            createdAt?: number | null;
        } = {},
    ): SketchGroup {
        const existingGroup = this.getGroupById(group.uuid);

        if (existingGroup) {
            this.syncNextSketchGroupNameIndexFromName(existingGroup.name);
            if (options.order !== undefined) {
                this.registerGroupOrder(existingGroup.uuid, options.order);
            }
            if (options.createdAt !== undefined && options.createdAt !== null) {
                this.registerGroupCreatedAt(existingGroup.uuid, options.createdAt);
            }
            return existingGroup;
        }

        if (this.isSketchScaleControlActive()) {
            this.stopSketchScaleControl();
        }

        const normalizedDisplayState = displayState ?? SketchDisplayState.Shown;

        this.syncNextSketchGroupNameIndexFromName(group.name);
        this._viewer.scene.add(group);
        this._groups.set(group.uuid, group);
        this.registerGroupOrder(group.uuid, options.order ?? null);
        if (options.createdAt !== undefined && options.createdAt !== null) {
            this.registerGroupCreatedAt(group.uuid, options.createdAt);
        } else {
            this.registerGroupCreatedAt(group.uuid);
        }
        this._displayStates.set(group.uuid, normalizedDisplayState);
        if (normalizedDisplayState === SketchDisplayState.Edit) {
            this._activeSketchGroup = group;
            this.activeSketchGroupCreatedInCurrentSession = false;
        }
        this.applyDisplayStateToGroup(group, normalizedDisplayState);
        this.definitionChangeHub.registerGroup(group);
        this.bindGroupEntityRemoved(group);
        this.publishLifecycleEvent(group, SketchGroupLifecycleEventType.Created, normalizedDisplayState);

        return group;
    }

    //------ 草图离线包定义持久化 开始------
    /**
     * 采集当前全部草图组的 `.puppy` 离线包记录。
     *
     * 设计说明：
     * 1. 记录按 sketchId 字典序排序，保证“场景无变化 → 连续保存内容一致”；
     * 2. 展示状态归一：编辑态保存为 `shown`（编辑会话是运行态，不属于文档事实）；
     * 3. 设计树顺序与创建时间显式写入信封，不能依赖按 sketchId 排序后的物理数组位置；
     * 4. 智能尺寸快照与草图定义在同一个信封中保存，两者不能覆盖顺序元数据；
     * 5. 序列化遇到 v1 未覆盖实体/关系类型会显式抛错，由调用方决定失败策略，禁止静默丢弃。
     *
     * @returns 草图扩展记录列表；无草图时返回空数组。
     */
    public createPuppySketchExtensionEntries(): PuppySketchExtensionEntry[] {
        return this.getAllGroups()
            .map((group, order) => {
                const sketch = this.sketchPersistenceSerializer.serialize(group);
                //------ 草图尺寸持久化 开始------
                /** 尺寸快照由领域仓库提供（按 dimensionId 字典序）；无尺寸时省略字段保持旧包字节形态。 */
                const dimensions = this.dimensionRepository.toPersistentSnapshot(sketch.sketchId).dimensions;
                //------ 草图尺寸持久化 结束------

                return {
                    displayState: (this._displayStates.get(group.uuid) === SketchDisplayState.Hidden
                        ? 'hidden'
                        : 'shown') as PuppySketchExtensionEntry['displayState'],
                    order: this.getGroupOrder(group.uuid) ?? order,
                    createdAt: this.getGroupCreatedAt(group.uuid),
                    sketch: dimensions.length > 0 ? {...sketch, dimensions} : sketch,
                };
            })
            .sort((left, right) => left.sketch.sketchId.localeCompare(right.sketch.sketchId));
    }

    /**
     * 从 `.puppy` 离线包草图扩展恢复草图组。
     *
     * 设计说明：
     * 1. 先按持久化 order 恢复逻辑顺序，再经 `restoreGroup` 统一接管场景挂载、索引、显示态与生命周期事件；
     * 2. 离线包导入是文档加载行为，不写入模型 Undo/Redo 历史（restoreGroup 本身不产生历史记录）；
     * 3. 重建保持全部持久身份不变，后续尺寸/引用可按持久 ID 反查；
     * 4. 旧版记录缺少 order/createdAt 时按 sketchList 位置兼容恢复，禁止拒绝既有离线包；
     * 5. 每个草图实体恢复完成后再灌入对应尺寸快照，保持实体、尺寸和设计树位置一致。
     *
     * @param sketchExtension `.puppy/assembly.json.extensions.sketches` 解析出的草图扩展。
     */
    public restorePuppySketchExtension(sketchExtension: PuppySketchExtension | null | undefined): void {
        if (!sketchExtension) {
            return;
        }
        /**
         * 幂等收口：restoreGroup 按运行时 uuid 判重，而每次反序列化都会产生新 uuid；
         * 这里必须按持久 ID 跳过已存在草图，避免 tab 反复切换时重复恢复同一草图。
         */
        const existingPersistentIds = new Set(
            this.getAllGroups().map((group) => group.persistentId),
        );
        /**
         * 离线包的物理数组按 sketchId 排序，只服务于保存字节确定性；
         * 恢复前必须按显式 order 重排。旧版记录没有 order 时保留原数组位置作为兼容兜底。
         */
        const orderedEntries = sketchExtension.sketchList
            .map((entry, sourceIndex) => ({
                entry,
                sourceIndex,
                order: entry.order ?? sourceIndex,
            }))
            .sort((left, right) => left.order - right.order || left.sourceIndex - right.sourceIndex);
        orderedEntries.forEach(({entry}, restoredOrder) => {
            if (existingPersistentIds.has(entry.sketch.sketchId)) {
                return;
            }
            const group = this.sketchPersistenceDeserializer.deserialize(entry.sketch);
            try {
                this.restoreGroup(
                    group,
                    entry.displayState === 'hidden' ? SketchDisplayState.Hidden : SketchDisplayState.Shown,
                    {
                        order: restoredOrder,
                        createdAt: entry.createdAt,
                    },
                );
                //------ 草图尺寸持久化 开始------
                /** 加载顺序：实体/关系重建之后灌尺寸；非法快照在此显式抛错，不静默降级。 */
                this.dimensionRepository.fromPersistentSnapshot({
                    schemaVersion: SKETCH_DIMENSION_SNAPSHOT_SCHEMA_VERSION_V1,
                    sketchId: entry.sketch.sketchId,
                    dimensions: entry.sketch.dimensions ?? [],
                });
                //------ 草图尺寸持久化 结束------

                /**
                 * 实体、关系和尺寸全部恢复后必须通过一次完整参数岛验证。
                 * 协调器内部原子回滚几何；此处再负责撤销刚挂载的领域对象，保证单草图加载全有或全无。
                 */
                if (!this._viewer.validateLoadedSketchParametricState(entry.sketch.sketchId)) {
                    throw new Error(`草图 ${entry.sketch.sketchId} 的参数方程状态无法收敛。`);
                }
                /**
                 * 离线包中的草图是已提交文档基线。倾斜投影等草图在加载验证期
                 * 可能产生数值校正 dirty，只有全部领域状态验证成功后才能在此确认。
                 * 这不派发 committed 事件，避免文档初始化误触发新一轮特征重建。
                 */
                group.acknowledgeDefinitionCommit();
            } catch (error) {
                this.dimensionRepository.deleteSketch(entry.sketch.sketchId);
                this.removeGroup(group.uuid);
                throw error;
            }
        });
    }
    //------ 草图离线包定义持久化 结束------

    /**
     * 删除指定草图组。
     *
     * 设计说明：
     * 1. 草图删除必须由管理器统一回收 scene、索引、显示态与定义事件监听；
     * 2. 若目标正处于缩放控制或编辑态，需要同步终止对应会话，避免残留悬挂引用；
     * 3. 删除成功后统一发布 `Removed` 生命周期事件，供宿主层刷新设计树。
     *
     * @param groupId 草图组 ID。
     * @returns 是否删除成功。
     */
    public removeGroup(groupId: string): boolean {
        const group = this.getGroupById(groupId);
        if (!group) {
            return false;
        }

        if (this._activeSketchGroup?.uuid === group.uuid) {
            this._activeSketchGroup = null;
            this.activeSketchGroupCreatedInCurrentSession = false;
        }
        if (this.isSketchScaleControlActive()) {
            this.stopSketchScaleControl();
        }

        this.definitionChangeHub.unregisterGroup(groupId);
        this.unbindGroupEntityRemoved(groupId);
        this._groups.delete(groupId);
        this.unregisterGroupOrder(groupId);
        this.unregisterGroupCreatedAt(groupId);
        this._displayStates.delete(groupId);
        this._viewer.scene.remove(group);
        group.dispose();
        this.publishLifecycleEvent(group, SketchGroupLifecycleEventType.Removed, null);
        return true;
    }

    /**
     * 判断当前是否已启用草图缩放控制。
     * @returns 当草图缩放已激活时返回 true。
     */
    public isSketchScaleControlActive(): boolean {
        return this.sketchScaleController.hasActiveSketchGroup();
    }

    /**
     * 启用指定草图组的缩放控制。
     *
     * 设计说明：
     * 1. 仅当当前不存在活动编辑草图时才允许开启缩放控制；
     * 2. 一旦任意草图进入编辑态，缩放控制应整体退场，避免编辑态与缩放态叠加；
     * 3. 因此这里对“编辑优先”做底层守卫，而不是要求宿主层手工规避。
     *
     * @param groupId 草图组 ID。
     */
    public startSketchScaleControl(groupId: string): void {
        if (this._activeSketchGroup) {
            if (this.isSketchScaleControlActive()) {
                this.stopSketchScaleControl();
            }
            return;
        }
        const group = this.getGroupById(groupId);
        if (!group) {
            throw new Error(`未找到目标草图组，无法开启草图缩放：${groupId}`);
        }
        this.sketchScaleController.enableSketchGroup(group);
    }

    /**
     * 关闭当前草图缩放控制。
     */
    public stopSketchScaleControl(): void {
        this.sketchScaleController.disableSketchGroup();
    }
    //------ 草图组索引能力 结束------

    /**
     * 开始一个新的草图组。
     * 如果已有活跃草图组，返回其 uuid；否则创建新的 SketchGroup 并添加到场景。
     * @param options 草图组创建选项。
     * @returns 草图组的 uuid，创建失败返回 null。
     */
    public startGroup(options: SketchGroupCreateOptions) : SketchGroup {
        if (this.isSketchScaleControlActive()) {
            this.stopSketchScaleControl();
        }
        // 创建 SketchGroup
        const group = new SketchGroup({
            name: this.allocateSketchGroupName(),
            referencePlane: options.referencePlane,
        });

        // 添加到场景
        this._viewer.scene.add(group);
        this._activeSketchGroup = group;
        this.activeSketchGroupCreatedInCurrentSession = true;
        this._groups.set(group.uuid, group);
        this.registerGroupOrder(group.uuid);
        this.registerGroupCreatedAt(group.uuid);
        this._displayStates.set(group.uuid, SketchDisplayState.Edit);
        //------ 草图显示态可见性同步 开始------
        /**
         * 新建草图组默认进入编辑态，必须立即与 Three.js group 的 visible 同步。
         * 否则后续只改 displayState 时，图形区可能仍残留旧可见性。
         */
        this.applyDisplayStateToGroup(group, SketchDisplayState.Edit);
        //------ 草图显示态可见性同步 结束------
        this.definitionChangeHub.registerGroup(group);
        this.bindGroupEntityRemoved(group);
        this.publishLifecycleEvent(group, SketchGroupLifecycleEventType.Created);

        return group
    }

    /**
     * 添加草图实体到当前活跃草图组。
     * @param entity 要添加的 THREE 对象。
     */
    public addSketchObject(entity: THREE.Object3D): void {
        if (!this._activeSketchGroup) {
            throw new Error('当前不存在活跃草图组，无法添加草图实体。');
        }

        this._viewer.addObject(entity, this._activeSketchGroup);
        /**
         * 草图编辑态下新增对象只会先拿到 overlay 基线层级；
         * 若不立即重跑一次编辑视觉分层，新增的当前草图线/点就可能停留在 200/210 基线，
         * 从而被其他草图的灰点覆盖。
         */
        this._viewer.syncSketchEditingVisualLayering();
    }

    /**
     * 结束当前草图组编辑（释放活跃草图组引用，但不从场景中移除）。
     *
     * @param options 结束选项。
     * @returns 草图组结束结果。
     */
    public endGroup(options: {
        discardEmpty?: boolean;
        publishLifecycleEvents?: boolean;
        selectEndedObjects?: boolean;
    } = {}): SketchGroupEndResult {
        if (!this._activeSketchGroup) {
            return {
                endedGroupId: null,
                endedGroup: null,
                removed: false,
                shouldCommitDefinitionChanges: false,
                displayState: null,
                displayStateChanged: false,
            };
        }

        const activeGroup = this._activeSketchGroup;
        const shouldDiscardEmptyGroup = this.activeSketchGroupCreatedInCurrentSession;
        this._activeSketchGroup = null;
        this.activeSketchGroupCreatedInCurrentSession = false;
        if (this.isSketchScaleControlActive()) {
            this.stopSketchScaleControl();
        }

        const shouldPublishLifecycleEvents = options.publishLifecycleEvents ?? true;
        if (
            (options.discardEmpty ?? false)
            && shouldDiscardEmptyGroup
            && !hasManagedSketchEntities(activeGroup)
        ) {
            this.definitionChangeHub.unregisterGroup(activeGroup.uuid);
            this.unbindGroupEntityRemoved(activeGroup.uuid);
            this._groups.delete(activeGroup.uuid);
            this.unregisterGroupOrder(activeGroup.uuid);
            this._displayStates.delete(activeGroup.uuid);
            this._viewer.scene.remove(activeGroup);
            activeGroup.dispose();
            const result: SketchGroupEndResult = {
                endedGroupId: activeGroup.uuid,
                endedGroup: activeGroup,
                removed: true,
                shouldCommitDefinitionChanges: false,
                displayState: null,
                displayStateChanged: false,
            };
            if (shouldPublishLifecycleEvents) {
                this.publishEndGroupLifecycle(result);
            }
            return result;
        }

        const currentDisplayState = this._displayStates.get(activeGroup.uuid);
        let nextDisplayState = currentDisplayState ?? SketchDisplayState.Shown;
        let displayStateChanged = false;
        if (currentDisplayState !== SketchDisplayState.Hidden) {
            nextDisplayState = SketchDisplayState.Shown;
            displayStateChanged = currentDisplayState !== SketchDisplayState.Shown;
            this._displayStates.set(activeGroup.uuid, nextDisplayState);
            clearSketchContourDisplays(activeGroup);
            //------ 草图显示态可见性同步 开始------
            /**
             * 草图退出编辑态后会切回展示态，必须同时刷新 group.visible，
             * 避免 displayState 与图形区真实显示状态分裂。
             */
            this.applyDisplayStateToGroup(activeGroup, nextDisplayState);
            //------ 草图显示态可见性同步 结束------
        }
        const result: SketchGroupEndResult = {
            endedGroupId: activeGroup.uuid,
            endedGroup: activeGroup,
            removed: false,
            shouldCommitDefinitionChanges: activeGroup.hasUncommittedDefinitionChanges(),
            displayState: nextDisplayState,
            displayStateChanged,
        };
        if (options.selectEndedObjects ?? true) {
            this.selectEndedSketchObjects(activeGroup);
        }
        if (shouldPublishLifecycleEvents) {
            this.publishEndGroupLifecycle(result);
        }
        return result;
    }

    /**
     * 按草图组 ID 选中已退出编辑的草图形状。
     *
     * 设计说明：
     * 1. `SketchSessionTerminator` 会在提交定义和最终显示态收口后再调用该入口；
     * 2. 直接调用 `endGroup()` 的低层路径仍保留默认立即选择行为；
     * 3. 若目标草图已被丢弃或不存在，直接返回，避免空草图退出污染选择集。
     *
     * @param groupId 已退出编辑的草图组 ID。
     */
    public selectEndedGroupSketchObjects(groupId: string | null): void {
        if (!groupId) {
            return;
        }
        const group = this._groups.get(groupId);
        if (!group) {
            return;
        }
        this.selectEndedSketchObjects(group);
    }

    /**
     * 自动选中刚退出编辑的草图形状。
     *
     * 设计说明：
     * 1. SolidWorks 在退出草图后会保留本次草图轮廓选择，后续“拉伸凸台”可直接吸收这些选择；
     * 2. 选择动作必须落在 cad-core 的草图会话收口处，而不是由 web-client 命令入口临时补齐；
     * 3. 统一委托 `EntitySelectionManager` 发布选择事件，避免绕过现有高亮、属性面板和特征命令监听链路。
     *
     * @param group 已结束编辑但保留在场景中的草图组。
     */
    private selectEndedSketchObjects(group: SketchGroup): void {
        const selectableObjects = this.collectPostExitSelectableSketchObjects(group);
        if (selectableObjects.length === 0) {
            return;
        }
        this._viewer.entitySelectionManager.selectEntitiesProgrammatically(selectableObjects);
    }

    /**
     * 收集退出草图后应进入选择集的形状对象。
     *
     * 设计说明：
     * 1. 控制点、辅助对象和绘制预览都不是用户语义中的“形状”，不应污染拉伸预选轮廓；
     * 2. 闭合集合、槽口这类复合轮廓只选择根对象，避免同时选择子边导致同一轮廓被重复吸收；
     * 3. 未归属复合轮廓的独立边对象仍作为普通草图形状保留选择状态。
     *
     * @param group 已结束编辑的草图组。
     * @returns 可交给选择管理器的草图形状对象列表。
     */
    private collectPostExitSelectableSketchObjects(group: SketchGroup): THREE.Object3D[] {
        const selectableObjects: THREE.Object3D[] = [];
        const selectedObjectIds = new Set<string>();

        group.getSketchObjects().forEach((object) => {
            if (!this.isPostExitSelectableSketchObject(object)) {
                return;
            }
            if (selectedObjectIds.has(object.uuid)) {
                return;
            }
            selectedObjectIds.add(object.uuid);
            selectableObjects.push(object);
        });

        return selectableObjects;
    }

    /**
     * 判断草图对象是否应在退出编辑后自动选中。
     *
     * @param object 候选草图对象。
     * @returns 应进入退出后选择集时返回 true。
     */
    private isPostExitSelectableSketchObject(object: THREE.Object3D): boolean {
        if (Reflect.get(object, 'isAuxiliaryObject') === true) {
            return false;
        }
        if (
            Reflect.get(object, 'isDrawingPreviewLine') === true
            || Reflect.get(object, 'isDrawingPreviewArc') === true
            || Reflect.get(object, 'isDrawingPreviewPoint') === true
        ) {
            return false;
        }
        if (isSketchPointObject(object)) {
            return false;
        }
        if (isSketchCollectionObject(object) || isSketchSlotObject(object)) {
            return true;
        }
        if (!isAbstractSketchEdgeObject(object)) {
            return false;
        }

        const closedCollection = Reflect.get(object, 'closedCollection');
        return !(closedCollection instanceof THREE.Object3D);
    }

    /**
     * 恢复对已有草图组的编辑。
     *
     * 设计说明：
     * 1. 与 `startGroup` / `endGroup` 构成 start / resume / end 三方法对称；
     * 2. 更新 `_activeSketchGroup` 与 `_displayStates`，并始终同步真实可见性；
     * 3. 幂等补齐 `definitionChangeHub.registerGroup`，确保历史恢复 / 运行时重建后的草图端点拖动仍能触发依赖特征重建；
     * 4. 拒绝跨草图切换：已有其他活动草图时直接返回 null，由上层 workflow 负责提示 UI。
     *
     * @param sketchGroupId 目标草图组 uuid。
     * @returns 草图组实例；目标不存在或被其它活动草图占用时返回 null。
     */
    public resumeGroup(sketchGroupId: string): SketchGroup | null {
        const group = this.getGroupById(sketchGroupId);
        if (!group) {
            return null;
        }
        if (this._activeSketchGroup && this._activeSketchGroup.uuid !== sketchGroupId) {
            return null;
        }
        if (this.isSketchScaleControlActive()) {
            this.stopSketchScaleControl();
        }

        const wasAlreadyActive = this._activeSketchGroup?.uuid === group.uuid;
        this._activeSketchGroup = group;
        if (!wasAlreadyActive) {
            this.activeSketchGroupCreatedInCurrentSession = false;
        }
        this.definitionChangeHub.registerGroup(group);

        const previousDisplayState = this._displayStates.get(group.uuid) ?? SketchDisplayState.Shown;
        this._displayStates.set(group.uuid, SketchDisplayState.Edit);
        //------ 草图显示态可见性同步 开始------
        /**
         * 恢复编辑态时必须无条件刷新 group.visible：
         * 运行时可能出现 displayState 已经是 Edit、但 Three.js group.visible 被其它链路置为 false 的状态分裂。
         * 右键“编辑草图”必须以 cad-core 的编辑会话为权威，把显示态和真实可见性重新对齐。
         */
        this.applyDisplayStateToGroup(group, SketchDisplayState.Edit);
        //------ 草图显示态可见性同步 结束------
        if (previousDisplayState !== SketchDisplayState.Edit) {
            this.publishLifecycleEvent(group, SketchGroupLifecycleEventType.DisplayStateChanged);
        }

        return group;
    }

    /**
     * 发布草图组结束后的生命周期事件。
     *
     * 设计说明：
     * 1. 会话结束后的提交与资源回收由 `Viewer` 统一编排，因此这里必须允许延后发布事件；
     * 2. 宿主层只应在底层真实完成“结束 + 提交 + 资源回收”之后再刷新设计树；
     * 3. 结束结果对象就是唯一输入，避免宿主层自行拼事件。
     *
     * @param result 草图组结束结果。
     */
    public publishEndGroupLifecycle(result: SketchGroupEndResult): void {
        if (!result.endedGroup) {
            return;
        }
        if (result.removed) {
            this.publishLifecycleEvent(result.endedGroup, SketchGroupLifecycleEventType.Removed, null);
            return;
        }
        if (result.displayStateChanged) {
            this.publishLifecycleEvent(
                result.endedGroup,
                SketchGroupLifecycleEventType.DisplayStateChanged,
                result.displayState,
            );
        }
        this.publishLifecycleEvent(result.endedGroup, SketchGroupLifecycleEventType.Ended, result.displayState);
    }

    /**
     * 获取指定草图组的显示状态。
     * @param groupId 草图组 uuid。
     * @returns 显示状态，不存在返回 undefined。
     */
    public getDisplayState(groupId: string): SketchDisplayState | undefined {
        return this._displayStates.get(this.resolveRuntimeGroupId(groupId));
    }

    /**
     * 设置指定草图组的显示状态。
     * @param groupId 草图组 uuid。
     * @param state 新的显示状态。
     * @param options 生命周期事件选项。
     */
    public setDisplayState(
        groupId: string,
        state: SketchDisplayState,
        options: {
            publishLifecycleEvent?: boolean;
        } = {},
    ): void {
        const runtimeGroupId = this.resolveRuntimeGroupId(groupId);
        this._displayStates.set(runtimeGroupId, state);
        const group = this._groups.get(runtimeGroupId);
        if (!group) {
            return;
        }
        if (state !== SketchDisplayState.Edit) {
            clearSketchContourDisplays(group);
        }
        //------ 草图显示态可见性同步 开始------
        /**
         * displayState 是草图显隐的权威语义，任何状态切换都必须同步到底层 group.visible。
         */
        this.applyDisplayStateToGroup(group, state);
        //------ 草图显示态可见性同步 结束------
        if (options.publishLifecycleEvent ?? true) {
            this.publishLifecycleEvent(group, SketchGroupLifecycleEventType.DisplayStateChanged);
        }
    }

    /**
     * 按历史快照恢复草图显示态与编辑态。
     *
     * 设计说明：
     * 1. 普通 `setDisplayState` 只负责显隐，不应该隐式抢占当前编辑草图；
     * 2. Undo/Redo 恢复的是模型历史真相，`Edit` 快照必须重新设置 `_activeSketchGroup`；
     * 3. 恢复为 `Shown/Hidden` 时，如果目标草图正处于编辑态，也必须清理活动引用。
     *
     * @param groupId 草图组 ID。
     * @param state 历史快照中的显示态。
     * @param options 生命周期事件选项。
     * @returns 是否命中目标草图并完成恢复。
     */
    public restoreDisplayStateFromHistory(
        groupId: string,
        state: SketchDisplayState,
        options: {
            publishLifecycleEvent?: boolean;
        } = {},
    ): boolean {
        const group = this._groups.get(groupId);
        if (!group) {
            return false;
        }
        if (this.isSketchScaleControlActive()) {
            this.stopSketchScaleControl();
        }

        if (state === SketchDisplayState.Edit) {
            const previousActiveGroup = this._activeSketchGroup;
            if (previousActiveGroup && previousActiveGroup.uuid !== groupId) {
                this._displayStates.set(previousActiveGroup.uuid, SketchDisplayState.Shown);
                clearSketchContourDisplays(previousActiveGroup);
                this.applyDisplayStateToGroup(previousActiveGroup, SketchDisplayState.Shown);
            }
            this._activeSketchGroup = group;
            this.activeSketchGroupCreatedInCurrentSession = false;
            this._displayStates.set(groupId, SketchDisplayState.Edit);
            this.applyDisplayStateToGroup(group, SketchDisplayState.Edit);
        } else {
            if (this._activeSketchGroup?.uuid === groupId) {
                this._activeSketchGroup = null;
                this.activeSketchGroupCreatedInCurrentSession = false;
            }
            this._displayStates.set(groupId, state);
            clearSketchContourDisplays(group);
            this.applyDisplayStateToGroup(group, state);
        }

        if (options.publishLifecycleEvent ?? true) {
            this.publishLifecycleEvent(group, SketchGroupLifecycleEventType.DisplayStateChanged);
        }
        return true;
    }

    /**
     * 释放草图组管理器资源。
     */
    public dispose(): void {
        this._activeSketchGroup = null;
        this.activeSketchGroupCreatedInCurrentSession = false;
        this._subscriptions.forEach((subscription) => subscription.unsubscribe());
        this._subscriptions.length = 0;
        Array.from(this._entityRemovedListenerBySketchId.keys()).forEach((sketchId) => {
            this.unbindGroupEntityRemoved(sketchId);
        });
        this.definitionChangeHub.dispose();
        this.sketchScaleController.dispose();
        this._groupOrder.length = 0;
        this._groupCreatedAt.clear();
        this._lifecycleSubject.complete();
        this._entityRemovedSubject.complete();
    }

    /**
     * 为真实设计树草图分配下一个默认名称。
     *
     * @returns 未被当前草图组占用的草图名称。
     */
    private allocateSketchGroupName(): string {
        while (this.hasSketchGroupName(`${SketchGroup.baseName}${this._nextSketchGroupNameIndex}`)) {
            this._nextSketchGroupNameIndex += 1;
        }

        const nextName = `${SketchGroup.baseName}${this._nextSketchGroupNameIndex}`;
        this._nextSketchGroupNameIndex += 1;
        return nextName;
    }

    /**
     * 判断当前管理器内是否已存在同名草图。
     *
     * @param name 待检查的草图名称。
     * @returns 是否存在同名草图。
     */
    private hasSketchGroupName(name: string): boolean {
        return Array.from(this._groups.values()).some((group) => group.name === name);
    }

    /**
     * 从恢复进来的草图名称同步默认名称序号上界。
     *
     * @param name 草图显示名称。
     */
    private syncNextSketchGroupNameIndexFromName(name: string): void {
        if (!name.startsWith(SketchGroup.baseName)) {
            return;
        }

        const indexText = name.slice(SketchGroup.baseName.length);
        if (!/^\d+$/.test(indexText)) {
            return;
        }

        const index = Number.parseInt(indexText, 10);
        if (Number.isSafeInteger(index) && index >= this._nextSketchGroupNameIndex) {
            this._nextSketchGroupNameIndex = index + 1;
        }
    }

    /**
     * 注册或移动草图组在设计树中的稳定顺序。
     *
     * @param groupId 草图组 ID。
     * @param order 目标顺序；为空时追加到末尾。
     */
    private registerGroupOrder(groupId: string, order: number | null = null): void {
        this.unregisterGroupOrder(groupId);

        if (typeof order !== 'number' || !Number.isFinite(order)) {
            this._groupOrder.push(groupId);
            return;
        }

        const normalizedOrder = Math.max(0, Math.min(Math.trunc(order), this._groupOrder.length));
        this._groupOrder.splice(normalizedOrder, 0, groupId);
    }

    /**
     * 从设计树稳定顺序中移除草图组 ID。
     *
     * @param groupId 草图组 ID。
     */
    private unregisterGroupOrder(groupId: string): void {
        const order = this._groupOrder.indexOf(groupId);

        if (order >= 0) {
            this._groupOrder.splice(order, 1);
        }
    }

    /**
     * 登记草图组在设计树中的创建时间戳。
     *
     * @param groupId 草图组 ID。
     * @param createdAt 创建时间戳；缺省时写入当前时间。
     */
    private registerGroupCreatedAt(groupId: string, createdAt?: number): void {
        if (this._groupCreatedAt.has(groupId)) {
            return;
        }
        this._groupCreatedAt.set(
            groupId,
            Number.isFinite(createdAt) ? Number(createdAt) : Date.now(),
        );
    }

    /**
     * 移除草图组在设计树中的创建时间戳。
     *
     * @param groupId 草图组 ID。
     */
    private unregisterGroupCreatedAt(groupId: string): void {
        this._groupCreatedAt.delete(groupId);
    }

    /**
     * 绑定草图组实体移除事件。
     *
     * @param group 草图组。
     */
    private bindGroupEntityRemoved(group: SketchGroup): void {
        if (this._entityRemovedListenerBySketchId.has(group.uuid)) {
            return;
        }
        const listener = (event: unknown): void => {
            this._entityRemovedSubject.next(event as SketchGroupEntityRemovedEvent);
        };
        group.addEventListener('sketch-entity-removed' as never, listener as never);
        this._entityRemovedListenerBySketchId.set(group.uuid, listener);
    }

    /**
     * 解绑草图组实体移除事件。
     *
     * @param sketchId 草图组 ID。
     */
    private unbindGroupEntityRemoved(sketchId: string): void {
        const listener = this._entityRemovedListenerBySketchId.get(sketchId);
        const group = this._groups.get(sketchId);
        if (listener && group) {
            group.removeEventListener('sketch-entity-removed' as never, listener as never);
        }
        this._entityRemovedListenerBySketchId.delete(sketchId);
    }

    /**
     * 发布草图组生命周期事件。
     *
     * @param group 草图组。
     * @param type 事件类型。
     * @param displayStateOverride 可选显示态覆盖。
     */
    private publishLifecycleEvent(
        group: SketchGroup,
        type: SketchGroupLifecycleEventType,
        displayStateOverride?: SketchDisplayState | null,
    ): void {
        this._lifecycleSubject.next({
            type,
            sketchId: group.uuid,
            persistentSketchId: group.persistentId,
            sketchName: group.name,
            displayState: displayStateOverride === undefined
                ? (this._displayStates.get(group.uuid) ?? null)
                : displayStateOverride,
            activeSketchId: this._activeSketchGroup?.uuid ?? null,
        });
    }

    //------ 草图显示态可见性同步 开始------
    /**
     * 把草图显示态同步到 Three.js Group 的可见性。
     *
     * 设计说明：
     * 1. `SketchDisplayState.Hidden` 才表示图形区需要真正隐藏；
     * 2. `Edit / Shown` 都应保持可见，保证编辑与普通展示语义一致；
     * 3. 统一集中在管理器内部收口，避免上层重复拼装显隐判断。
     *
     * @param group 草图组实例。
     * @param state 目标显示态。
     */
    private applyDisplayStateToGroup(group: SketchGroup, state: SketchDisplayState): void {
        group.visible = state !== SketchDisplayState.Hidden;
        // 显隐属于普通视图脏状态，由统一帧调度器在同一宿主帧内合并提交。
        this._viewer.requestRender();
    }
    //------ 草图显示态可见性同步 结束------

    /**
     * 绑定实体选择事件。
     * 说明：
     * 1. 当实体选择结果为空时自动关闭草图缩放；
     * 2. 仅响应真正“没有选中任何对象”的事件；
     * 3. 不干预其它正常选中流程。
     */
    private bindSelectionEvents(): void {
        const entitySelectionManager = this._viewer.entitySelectionManager;
        if (!entitySelectionManager) {
            throw new Error('草图组管理器初始化失败：EntitySelectionManager 不存在。');
        }
        const noSelectionSubscription = entitySelectionManager
            .observe(EntityInteractionEventType.EntitySelected)
            .subscribe((payload) => {
                if (payload.objectId !== null) {
                    return;
                }
                if (!this.isSketchScaleControlActive()) {
                    return;
                }
                this.stopSketchScaleControl();
            });
        this._subscriptions.push(noSelectionSubscription);
    }
}
